import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Calculator,
  Home,
  Building2,
  Castle,
  Warehouse,
  ArrowRight,
  Package,
  Truck,
  Wrench,
  Box,
  Info,
  TrendingDown,
} from "lucide-react";

interface PropertyType {
  id: string;
  label: string;
  icon: typeof Home;
  basePrice: number;
  volumeM3: number;
}

interface Service {
  id: string;
  label: string;
  icon: typeof Package;
  pricePercentage: number;
}

const propertyTypes: PropertyType[] = [
  { id: "studio", label: "Studio", icon: Building2, basePrice: 350, volumeM3: 15 },
  { id: "apartment", label: "Appartement", icon: Building2, basePrice: 550, volumeM3: 30 },
  { id: "house", label: "Tussenwoning", icon: Home, basePrice: 850, volumeM3: 50 },
  { id: "corner", label: "Hoekwoning", icon: Home, basePrice: 1050, volumeM3: 65 },
  { id: "detached", label: "Vrijstaand", icon: Castle, basePrice: 1450, volumeM3: 90 },
  { id: "villa", label: "Villa", icon: Castle, basePrice: 2200, volumeM3: 120 },
];

const services: Service[] = [
  { id: "packing", label: "Inpakservice", icon: Package, pricePercentage: 35 },
  { id: "lift", label: "Verhuislift", icon: Truck, pricePercentage: 15 },
  { id: "assembly", label: "Montage/demontage", icon: Wrench, pricePercentage: 20 },
  { id: "storage", label: "Tijdelijke opslag", icon: Box, pricePercentage: 25 },
];

const MovingCostCalculator = () => {
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState<string>("apartment");
  const [distance, setDistance] = useState<number[]>([25]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const toggleService = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculation = useMemo(() => {
    const property = propertyTypes.find((p) => p.id === selectedProperty);
    if (!property) return { min: 0, max: 0, average: 0, savings: 0 };

    // Base price from property type
    let basePrice = property.basePrice;

    // Distance factor (€2 per km after 10km)
    const distanceKm = distance[0];
    const distanceCost = distanceKm > 10 ? (distanceKm - 10) * 2 : 0;

    // Services cost
    const servicesCost = selectedServices.reduce((total, serviceId) => {
      const service = services.find((s) => s.id === serviceId);
      return total + (service ? (basePrice * service.pricePercentage) / 100 : 0);
    }, 0);

    const totalBase = basePrice + distanceCost + servicesCost;

    // Price range (different companies have different prices)
    const min = Math.round(totalBase * 0.8);
    const max = Math.round(totalBase * 1.3);
    const average = Math.round((min + max) / 2);
    const savings = Math.round(max - min);

    return { min, max, average, savings };
  }, [selectedProperty, distance, selectedServices]);

  const handleGetQuotes = () => {
    const property = propertyTypes.find((p) => p.id === selectedProperty);
    navigate(`/quote?property=${selectedProperty}&distance=${distance[0]}&services=${selectedServices.join(",")}&volume=${property?.volumeM3 || 30}`);
  };

  return (
    <section className="py-20 md:py-28 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-0 px-4 py-1.5">
            <Calculator className="w-4 h-4 mr-1" />
            Verhuiskosten Calculator
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Wat kost uw verhuizing?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Krijg direct een indicatie van uw verhuiskosten. Vul de gegevens in en ontvang een prijsrange.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Calculator Form */}
            <div className="lg:col-span-3 space-y-8">
              {/* Property Type */}
              <div className="bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-primary" />
                  Type woning
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {propertyTypes.map((property) => (
                    <button
                      key={property.id}
                      onClick={() => setSelectedProperty(property.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        selectedProperty === property.id
                          ? "border-primary bg-primary/5 shadow-md"
                          : "border-border/50 hover:border-primary/30 hover:bg-muted/50"
                      }`}
                    >
                      <property.icon
                        className={`w-6 h-6 ${
                          selectedProperty === property.id
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                      <span
                        className={`text-sm font-medium ${
                          selectedProperty === property.id
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        {property.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ~{property.volumeM3}m³
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Distance */}
              <div className="bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-primary" />
                  Afstand
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Verhuisafstand</span>
                    <span className="font-semibold text-foreground">{distance[0]} km</span>
                  </div>
                  <Slider
                    value={distance}
                    onValueChange={setDistance}
                    min={5}
                    max={200}
                    step={5}
                    className="py-2"
                    aria-label="Verhuisafstand in kilometers"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>5 km</span>
                    <span>200 km</span>
                  </div>
                </div>
              </div>

              {/* Extra Services */}
              <div className="bg-card rounded-2xl border border-border/50 p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  Extra diensten
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedServices.includes(service.id)
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/30"
                      }`}
                    >
                      <Checkbox
                        checked={selectedServices.includes(service.id)}
                        onCheckedChange={() => toggleService(service.id)}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <service.icon
                        className={`w-5 h-5 ${
                          selectedServices.includes(service.id)
                            ? "text-primary"
                            : "text-muted-foreground"
                        }`}
                      />
                      <div className="flex-1">
                        <span
                          className={`text-sm font-medium ${
                            selectedServices.includes(service.id)
                              ? "text-primary"
                              : "text-foreground"
                          }`}
                        >
                          {service.label}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          +{service.pricePercentage}%
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl border border-border/50 p-6 sticky top-24 space-y-6">
                <div className="text-center">
                  <h3 className="font-semibold text-foreground mb-2">
                    Geschatte kosten
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Indicatie o.b.v. gemiddelde marktprijzen
                  </p>
                </div>

                {/* Price Range */}
                <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 text-center">
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-lg text-muted-foreground">€</span>
                    <span className="text-4xl font-bold text-foreground">
                      {calculation.min.toLocaleString()}
                    </span>
                    <span className="text-lg text-muted-foreground mx-2">-</span>
                    <span className="text-lg text-muted-foreground">€</span>
                    <span className="text-4xl font-bold text-foreground">
                      {calculation.max.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Gemiddeld:{" "}
                    <strong className="text-foreground">
                      €{calculation.average.toLocaleString()}
                    </strong>
                  </p>
                </div>

                {/* Savings Highlight */}
                <div className="flex items-center gap-3 p-4 rounded-xl bg-success/10 border border-success/20">
                  <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-success-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-success-dark">
                      Bespaar tot €{calculation.savings.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Door offertes te vergelijken
                    </p>
                  </div>
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Dit is een indicatie. Vraag gratis verhuisoffertes aan voor exacte
                    prijzen van verhuisbedrijven in uw regio.
                  </p>
                </div>

                {/* CTA */}
                <Button
                  onClick={handleGetQuotes}
                  size="lg"
                  className="w-full h-14 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-xl shadow-lg shadow-accent/25 group"
                >
                  Ontvang gratis verhuisoffertes
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  100% gratis • Geen verplichtingen
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MovingCostCalculator;
